import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Globe, DollarSign } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is an international invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'An international invoice is a billing document used when a business or freelancer provides services or goods to a client in another country. It is typically issued in a foreign currency (USD, GBP, EUR, etc.) and does not include domestic taxes like GST or VAT unless specifically required by the importing country.',
      },
    },
    {
      '@type': 'Question',
      name: 'What currency can I use on an international invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can use any currency on an international invoice: USD, GBP, EUR, CAD, AUD, SGD, or any other. Invilic lets you select the currency for your invoice. Indian freelancers typically bill foreign clients in USD or the client\'s local currency.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need to charge GST or VAT on international invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Generally no. Services exported from India to foreign clients are considered "zero-rated" under GST, meaning 0% GST applies and you are not required to charge Indian GST. However, the rules vary depending on the nature of the service and whether the client is a business or individual. Consult a tax professional for your specific situation.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can Indian freelancers use Invilic to invoice foreign clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic\'s international invoice type is built for exactly this. Indian freelancers, consultants, and businesses billing overseas clients. Choose your currency, add your services, and download a professional PDF to send to your foreign client.',
      },
    },
    {
      '@type': 'Question',
      name: 'What details should an international invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'An international invoice should include: your name/business name and country; client name and country; invoice number and date; currency; service description and amounts; payment due date; bank details or wire transfer information (SWIFT/BIC code, IBAN, or ACH routing number for USD payments).',
      },
    },
  ],
};

const currencies = [
  { code: 'USD', label: 'US Dollar', flag: '🇺🇸' },
  { code: 'GBP', label: 'British Pound', flag: '🇬🇧' },
  { code: 'EUR', label: 'Euro', flag: '🇪🇺' },
  { code: 'CAD', label: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', label: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'SGD', label: 'Singapore Dollar', flag: '🇸🇬' },
];

export default function InternationalInvoiceGeneratorPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="International Invoice Generator: Free Multi-Currency Invoice | Invilic"
        description="Create international invoices online free. Bill foreign clients in USD, GBP, EUR & more. Perfect for Indian freelancers exporting services. Download PDF instantly."
        canonical="https://www.invilic.com/international-invoice-generator"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Multi-Currency · Global Clients
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            International Invoice Generator
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Create professional international invoices for foreign clients in any currency. Perfect for Indian freelancers, consultants, and businesses exporting services overseas. Download PDF free.
          </p>
          <Link
            to="/create/international"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-indigo-600/20 transition-all duration-200 text-lg"
          >
            Create International Invoice <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Currencies */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Supported Currencies
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
            {currencies.map(({ code, label, flag }) => (
              <div key={code} className="text-center p-4 rounded-xl border border-indigo-100 dark:border-indigo-800/30 bg-indigo-50 dark:bg-indigo-900/10">
                <div className="text-2xl mb-1">{flag}</div>
                <div className="font-bold text-gray-900 dark:text-white text-sm">{code}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            + any other currency, enter the symbol and code manually
          </p>
        </section>

        {/* What's included */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's on Your International Invoice
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your business name, country & contact',
              'Foreign client name and country',
              'Invoice number and date',
              'Currency selection (USD, GBP, EUR, etc.)',
              'Service line items with amounts',
              'Payment due date',
              'Bank wire details (SWIFT, IBAN)',
              'Terms and notes',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800/30 bg-indigo-50 dark:bg-indigo-900/10">
                <CheckCircle className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Indian freelancers callout */}
        <section className="mb-16 p-8 rounded-2xl border border-indigo-100 dark:border-indigo-800/40 bg-indigo-50 dark:bg-indigo-900/20">
          <div className="flex items-start gap-4">
            <div className="text-3xl">🇮🇳</div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                For Indian Freelancers Billing Foreign Clients
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4">
                If you are an Indian freelancer or consultant exporting services (software development, design, writing, consulting) to clients in the US, UK, EU, or elsewhere. Typically you do not charge Indian GST on these exports. Use the International Invoice type instead of the GST invoice.
              </p>
              <div className="space-y-2">
                {[
                  'Bill in USD, GBP, EUR or the client\'s preferred currency',
                  'Include your bank SWIFT code and account details for wire transfers',
                  'Mention "Export of Services" in the description if required',
                  'Keep a copy for FEMA/RBI compliance if receipts exceed $10,000',
                ].map(tip => (
                  <div key={tip} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <DollarSign className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" /> {tip}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            International Invoice: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your International Invoice for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Bill foreign clients professionally in any currency. PDF in seconds.</p>
          <Link
            to="/create/international"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-indigo-600/20 transition-all duration-200 text-lg"
          >
            <Globe className="w-5 h-5" /> Create International Invoice
          </Link>
        </section>
      </main>
    </div>
  );
}

